import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CalendarDays, ChevronRight, Copy, Hash, MessageCircle, Plus,
  Send, Sparkles, Users, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api, formatApiError, pictureUrl } from '../lib/api';
import { Seo } from '../components/Seo';
import MemberAIChat from '../components/member/MemberAIChat';

const cardClass = 'rounded-xl border border-white/10 bg-[#100c14]';
const inputClass = 'w-full rounded-lg border border-white/10 bg-black/35 px-3.5 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#ff2e2e]';

const MemberCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [view, setView] = useState('home');
  const [mobilePane, setMobilePane] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [deleteRequestDialogOpen, setDeleteRequestDialogOpen] = useState(false);
  const [deleteRequestReason, setDeleteRequestReason] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [groupLogo, setGroupLogo] = useState(null);
  const [groupBanner, setGroupBanner] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [channelName, setChannelName] = useState('');
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const userId = user && user !== false ? user.user_id : null;
  const activeGroupId = activeGroup?.id;
  const activeChannelId = activeChannel?.id;

  useEffect(() => {
    if (user === false) navigate('/login', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!userId) return undefined;
    let active = true;
    api.get('/member-center/chat/groups')
      .then(({ data }) => {
        if (!active) return;
        const availableGroups = data.groups || [];
        setGroups(availableGroups);
        setActiveGroup((current) => availableGroups.find((group) => group.id === current?.id)
          || availableGroups.find((group) => group.is_main)
          || availableGroups[0]
          || null);
      })
      .catch((error) => { if (active) setChatError(formatApiError(error, 'Kunne ikkje hente medlemsgruppene.')); })
      .finally(() => { if (active) setLoadingGroups(false); });
    return () => { active = false; };
  }, [userId]);

  useEffect(() => {
    if (!activeGroupId) {
      setChannels([]);
      setActiveChannel(null);
      return undefined;
    }
    let active = true;
    setActiveChannel(null);
    api.get(`/member-center/chat/groups/${encodeURIComponent(activeGroupId)}/channels`)
      .then(({ data }) => {
        if (!active) return;
        const availableChannels = data.channels || [];
        setChannels(availableChannels);
        setActiveChannel(availableChannels[0] || null);
        setMobilePane('channels');
      })
      .catch((error) => { if (active) setChatError(formatApiError(error, 'Kunne ikkje hente kanalane.')); });
    return () => { active = false; };
  }, [activeGroupId]);

  useEffect(() => {
    if (!activeGroupId || !activeChannelId) {
      setMessages([]);
      return undefined;
    }
    let active = true;
    const loadMessages = async (initial = false) => {
      if (initial) setLoadingMessages(true);
      try {
        const { data } = await api.get(`/member-center/chat/groups/${encodeURIComponent(activeGroupId)}/channels/${encodeURIComponent(activeChannelId)}/messages`);
        if (active) {
          setMessages(data.messages || []);
          setChatError('');
        }
      } catch (error) {
        if (active) setChatError(formatApiError(error, 'Kunne ikkje hente meldingane.'));
      } finally {
        if (initial && active) setLoadingMessages(false);
      }
    };
    loadMessages(true);
    const poll = window.setInterval(() => loadMessages(), 4000);
    return () => { active = false; window.clearInterval(poll); };
  }, [activeGroupId, activeChannelId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const refreshGroups = async (selectedId) => {
    const { data } = await api.get('/member-center/chat/groups');
    const nextGroups = data.groups || [];
    setGroups(nextGroups);
    const nextActive = nextGroups.find((group) => group.id === selectedId)
      || nextGroups.find((group) => group.id === activeGroup?.id)
      || nextGroups.find((group) => group.is_main)
      || nextGroups[0]
      || null;
    setActiveGroup(nextActive);
    return nextActive;
  };

  const uploadGroupImage = async (file, kind) => {
    if (!file) return '';
    const formData = new FormData();
    formData.append('kind', kind);
    formData.append('file', file);
    const { data } = await api.post('/member-center/chat/assets', formData);
    return data.url;
  };

  const createGroup = async (event) => {
    event.preventDefault();
    setDialogBusy(true);
    setDialogError('');
    try {
      const [logoUrl, bannerUrl] = await Promise.all([
        uploadGroupImage(groupLogo, 'logo'),
        uploadGroupImage(groupBanner, 'banner'),
      ]);
      const { data } = await api.post('/member-center/chat/groups', {
        name: groupName,
        description: groupDescription,
        logo_url: logoUrl,
        banner_url: bannerUrl,
      });
      await refreshGroups(data.group.id);
      setGroupDialogOpen(false);
      setGroupName('');
      setGroupDescription('');
      setGroupLogo(null);
      setGroupBanner(null);
      setView('chats');
      toast.success('Gruppa er oppretta. Del gruppekoden for å invitere medlemmer.');
    } catch (error) {
      setDialogError(formatApiError(error, 'Kunne ikkje opprette gruppa.'));
    } finally {
      setDialogBusy(false);
    }
  };

  const joinGroup = async (event) => {
    event.preventDefault();
    setDialogBusy(true);
    setDialogError('');
    try {
      const { data } = await api.post('/member-center/chat/groups/join', { code: joinCode });
      await refreshGroups(data.group.id);
      setJoinDialogOpen(false);
      setJoinCode('');
      setView('chats');
      toast.success(`Du er no med i ${data.group.name}.`);
    } catch (error) {
      setDialogError(formatApiError(error, 'Gruppekoden kunne ikkje brukast.'));
    } finally {
      setDialogBusy(false);
    }
  };

  const createChannel = async (event) => {
    event.preventDefault();
    if (!activeGroup) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const { data } = await api.post(`/member-center/chat/groups/${encodeURIComponent(activeGroup.id)}/channels`, { name: channelName });
      setChannels((current) => [...current, data.channel]);
      setActiveChannel(data.channel);
      setChannelName('');
      setChannelDialogOpen(false);
      setMobilePane('chat');
    } catch (error) {
      setDialogError(formatApiError(error, 'Kunne ikkje opprette chatten.'));
    } finally {
      setDialogBusy(false);
    }
  };

  const requestGroupDeletion = async (event) => {
    event.preventDefault();
    if (!activeGroup?.id) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      await api.post(`/member-center/chat/groups/${encodeURIComponent(activeGroup.id)}/deletion-requests`, { reason: deleteRequestReason });
      const updatedGroup = { ...activeGroup, deletion_requested: true };
      setActiveGroup(updatedGroup);
      setGroups((current) => current.map((group) => group.id === updatedGroup.id ? updatedGroup : group));
      setDeleteRequestDialogOpen(false);
      setDeleteRequestReason('');
      toast.success('Sletteførespurnaden er sendt til administratorane.');
    } catch (error) {
      setDialogError(formatApiError(error, 'Kunne ikkje sende sletteførespurnaden.'));
    } finally {
      setDialogBusy(false);
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = messageText.trim();
    if (!text || !activeGroup || !activeChannel || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/member-center/chat/groups/${encodeURIComponent(activeGroup.id)}/channels/${encodeURIComponent(activeChannel.id)}/messages`, { text });
      setMessages((current) => [...current, data.message]);
      setMessageText('');
      setChatError('');
    } catch (error) {
      setChatError(formatApiError(error, 'Meldinga vart ikkje send.'));
    } finally {
      setSending(false);
    }
  };

  const copyJoinCode = async () => {
    try {
      await navigator.clipboard.writeText(activeGroup.join_code);
      toast.success('Gruppekoden er kopiert.');
    } catch {
      toast.error('Klarte ikkje kopiere gruppekoden.');
    }
  };

  if (!user || user === false) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60">Lastar medlemssenter…</div>;
  }

  return (
    <div className="min-h-screen bg-[#070609] text-white" data-testid="member-center-page">
      <Seo title="Medlemssenter | Refuel Weekend" description="Chatter og medlemsgrupper for Refuel Weekend." path="/medlemssenter" noindex />
      <Navbar />
      <main className="mx-auto min-h-[calc(100vh-4rem)] max-w-[1600px] px-4 py-8 md:px-8">
        {view === 'home' ? (
          <>
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[#ff5555]">Refuel Weekend · Medlem</p>
                <h1 className="text-3xl font-bold md:text-4xl" style={{ fontFamily: "'Oktah Round', sans-serif" }}>Medlemssenter</h1>
                <p className="mt-2 text-sm text-white/55">Samtalar, folk og det som skjer rundt Refuel.</p>
              </div>
              <button type="button" onClick={() => navigate('/medlem')} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/75 transition hover:border-white/35 hover:text-white"><ArrowLeft size={16} /> Medlemsprofil</button>
            </div>
            <section aria-label="Medlemssenter-val" className="grid gap-4 md:grid-cols-3">
              <button type="button" data-testid="member-center-chats-card" onClick={() => setView('chats')} className={`${cardClass} group min-h-[230px] p-6 text-left transition hover:border-[#ff2e2e]/55 hover:bg-[#160d12] focus:outline-none focus:ring-2 focus:ring-[#ff2e2e]`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#ff2e2e]/15 text-[#ff5c5c]"><MessageCircle size={21} /></div>
                <div className="mt-9 flex items-end justify-between gap-3">
                  <div><h2 className="text-xl font-bold">Chatter</h2><p className="mt-2 text-sm text-white/55">Refuel Chatten og medlemsgrupper.</p></div>
                  <ChevronRight className="mb-1 shrink-0 text-[#ff5555] transition-transform group-hover:translate-x-1" />
                </div>
              </button>
              <button type="button" data-testid="member-center-ai-card" onClick={() => setView('ai')} className={`${cardClass} group min-h-[230px] p-6 text-left transition hover:border-emerald-400/50 hover:bg-emerald-950/20 focus:outline-none focus:ring-2 focus:ring-emerald-400`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300"><Sparkles size={21} /></div>
                <div className="mt-9 flex items-end justify-between gap-3">
                  <div><h2 className="text-xl font-bold">AI-chat</h2><p className="mt-2 text-sm text-white/55">Still spørsmål til Refuel-assistenten.</p></div>
                  <ChevronRight className="mb-1 shrink-0 text-emerald-300 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
              <div data-testid="member-center-events-card" className={`${cardClass} relative min-h-[230px] overflow-hidden p-6 opacity-75`}>
                <span className="absolute right-5 top-5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/45">Kommer snart</span>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/5 text-white/40"><CalendarDays size={21} /></div>
                <div className="mt-9"><h2 className="text-xl font-bold text-white/60">Arrangementer</h2><p className="mt-2 text-sm text-white/40">Samla oversikt over treff og aktivitetar.</p></div>
              </div>
              <div data-testid="member-center-community-card" className={`${cardClass} relative min-h-[230px] overflow-hidden p-6 opacity-75`}>
                <span className="absolute right-5 top-5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/45">Kommer snart</span>
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/5 text-white/40"><Sparkles size={21} /></div>
                <div className="mt-9"><h2 className="text-xl font-bold text-white/60">Medlemsfordeler</h2><p className="mt-2 text-sm text-white/40">Tilbod og fordelar for medlemmene.</p></div>
              </div>
            </section>
          </>
        ) : view === 'ai' ? (
          <section className="mx-auto max-w-4xl">
            <button type="button" onClick={() => setView('home')} className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white"><ArrowLeft size={14} /> Medlemssenter</button>
            <MemberAIChat />
          </section>
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <button type="button" onClick={() => setView('home')} className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white"><ArrowLeft size={14} /> Medlemssenter</button>
                <h1 className="truncate text-2xl font-bold">Chatter</h1>
              </div>
              <div className="hidden items-center gap-2 text-xs text-white/40 sm:flex"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Medlemschat</div>
            </div>

            <div className="grid h-[min(74vh,780px)] min-h-[560px] grid-cols-1 overflow-hidden rounded-xl border border-white/10 bg-[#0c0a0e] md:grid-cols-[210px_220px_minmax(0,1fr)]" data-testid="member-chat-workspace">
              <aside className={`${mobilePane === 'groups' ? 'flex' : 'hidden'} min-h-0 flex-col border-b border-white/10 bg-[#100d13] md:flex md:border-b-0 md:border-r`}>
                <div className="flex h-14 items-center justify-between border-b border-white/5 px-4">
                  <h2 className="text-xs font-bold uppercase tracking-[0.12em] text-white/55">Grupper</h2>
                  <div className="flex gap-1">
                    <button type="button" data-testid="join-chat-group-button" onClick={() => { setDialogError(''); setJoinDialogOpen(true); }} title="Bli med i gruppe" className="flex h-8 w-8 items-center justify-center rounded-md text-white/55 hover:bg-white/5 hover:text-white"><Users size={16} /></button>
                    <button type="button" data-testid="create-chat-group-button" onClick={() => { setDialogError(''); setGroupDialogOpen(true); }} title="Opprett gruppe" className="flex h-8 w-8 items-center justify-center rounded-md text-white/55 hover:bg-white/5 hover:text-[#ff6262]"><Plus size={18} /></button>
                  </div>
                </div>
                <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
                  {loadingGroups ? <p className="px-3 py-4 text-sm text-white/40">Lastar grupper…</p> : groups.map((group) => (
                    <button key={group.id} type="button" data-testid={`chat-group-${group.id}`} onClick={() => { setActiveGroup(group); setMobilePane('channels'); setChatError(''); }} className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition ${activeGroup?.id === group.id ? 'bg-[#ff2e2e]/15 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}>
                      {group.logo_url ? <img src={pictureUrl(group.logo_url)} alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" /> : <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${group.is_main ? 'bg-[#ff2e2e] text-black' : 'bg-white/10 text-white/75'}`}>{group.name.slice(0, 1).toUpperCase()}</span>}
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{group.name}</span>
                      {group.is_main && <span className="text-[9px] uppercase tracking-wider text-[#ff7777]">Hovud</span>}
                    </button>
                  ))}
                  {!loadingGroups && groups.length === 0 && <p className="px-3 py-4 text-sm text-white/40">Ingen grupper enno.</p>}
                </div>
                <div className="border-t border-white/5 p-3 text-[11px] leading-5 text-white/35">Lag ei gruppe eller bruk ein gruppekode for å bli med.</div>
              </aside>

              <aside className={`${mobilePane === 'channels' ? 'flex' : 'hidden'} min-h-0 flex-col border-b border-white/10 bg-[#0e0b10] md:flex md:border-b-0 md:border-r`}>
                <div className="flex h-14 items-center justify-between gap-2 border-b border-white/5 px-4">
                  <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wider text-white/35">Vald gruppe</p><p className="truncate text-sm font-semibold">{activeGroup?.name || 'Vel ei gruppe'}</p></div>
                  {activeGroup && <button type="button" data-testid="create-chat-channel-button" onClick={() => { setDialogError(''); setChannelDialogOpen(true); }} title="Opprett chat" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/50 hover:bg-white/5 hover:text-[#ff6262]"><Plus size={18} /></button>}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.12em] text-white/35">Tekstchatter</p>
                  <div className="space-y-1">
                    {channels.map((channel) => (
                      <button key={channel.id} type="button" data-testid={`chat-channel-${channel.id}`} onClick={() => { setActiveChannel(channel); setMobilePane('chat'); }} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition ${activeChannel?.id === channel.id ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white/80'}`}>
                        <Hash size={15} className="shrink-0 text-white/35" /><span className="truncate">{channel.name}</span>
                      </button>
                    ))}
                    {!activeGroup && <p className="px-2 py-3 text-xs text-white/35">Vel ei gruppe først.</p>}
                  </div>
                </div>
                {activeGroup?.join_code && (
                  <div className="border-t border-white/5 p-3">
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-white/35">Gruppekode</p>
                    <button type="button" onClick={copyJoinCode} title="Kopier gruppekode" className="flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2.5 font-mono text-sm tracking-[0.12em] text-white/80 hover:border-white/25"><span>{activeGroup.join_code}</span><Copy size={14} className="text-white/45" /></button>
                  </div>
                )}
                {activeGroup?.can_request_deletion && (
                  <div className="border-t border-white/5 p-3">
                    {activeGroup.deletion_requested ? (
                      <p className="text-xs leading-5 text-amber-200/75">Sletteførespurnad sendt. Ein administrator må godkjenne henne.</p>
                    ) : (
                      <button type="button" onClick={() => { setDialogError(''); setDeleteRequestDialogOpen(true); }} data-testid="request-group-deletion-button" className="inline-flex items-center gap-2 text-xs font-semibold text-red-300/80 transition hover:text-red-200"><Trash2 size={14} /> Be om å slette gruppa</button>
                    )}
                  </div>
                )}
              </aside>

              <section className={`${mobilePane === 'chat' ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-col bg-[#0b090d] md:flex`} aria-label="Medlemschat">
                <header className="relative flex min-h-14 items-center gap-3 overflow-hidden border-b border-white/10 px-4 py-2.5">
                  {activeGroup?.banner_url && <img src={pictureUrl(activeGroup.banner_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
                  <button type="button" onClick={() => setMobilePane('channels')} className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/45 hover:bg-white/5 md:hidden" aria-label="Tilbake til chatter"><ArrowLeft size={17} /></button>
                  <div className="relative min-w-0 flex-1">
                    <p className="truncate text-sm font-bold"><span className="mr-1 text-white/35">#</span>{activeChannel?.name || 'Vel ein chat'}</p>
                    <p className="truncate text-[11px] text-white/40">{activeGroup?.name || 'Medlemssenter'}{activeGroup?.description ? ` · ${activeGroup.description}` : ''}</p>
                  </div>
                  <div className="relative hidden items-center gap-2 text-xs text-white/40 sm:flex"><Users size={15} /> {activeGroup?.member_count || (activeGroup?.is_main ? 'Alle medlemmer' : 'Medlemmer')}</div>
                </header>
                {chatError && <p role="alert" data-testid="member-chat-error" className="border-b border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-200">{chatError}</p>}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 md:px-7" data-testid="chat-messages">
                  {loadingMessages ? <p className="text-sm text-white/40">Lastar meldingar…</p> : messages.length === 0 ? (
                    <div className="flex h-full min-h-[230px] flex-col items-center justify-center text-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 text-white/35"><Hash size={22} /></span>
                      <h2 className="mt-4 text-lg font-bold">Velkomen til #{activeChannel?.name || 'chatten'}</h2>
                      <p className="mt-1 max-w-xs text-sm text-white/45">Her kan de diskutere, dele idear og ta kontakt med Refuel-teamet.</p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {messages.map((message) => (
                        <article key={message.id} data-testid="chat-message" className="flex gap-3">
                          {message.picture ? <img src={pictureUrl(message.picture)} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ff2e2e]/20 text-xs font-bold text-[#ff7777]">{message.name?.slice(0, 1).toUpperCase()}</span>}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                              <p className="text-sm font-bold text-white/90">{message.name}</p>
                              <time className="text-[10px] text-white/35">{new Date(message.created_at).toLocaleString()}</time>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-white/75">{message.text}</p>
                          </div>
                        </article>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>
                <form onSubmit={sendMessage} className="border-t border-white/10 p-3 md:px-5 md:py-4">
                  <div className="flex items-end gap-2 rounded-xl border border-white/10 bg-white/[0.035] p-2 focus-within:border-white/20">
                    <label htmlFor="chat-message-input" className="sr-only">Skriv ei melding</label>
                    <textarea id="chat-message-input" data-testid="chat-message-input" rows={1} maxLength={2000} value={messageText} onChange={(event) => setMessageText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} disabled={!activeChannel} placeholder={activeChannel ? `Skriv ei melding i #${activeChannel.name}` : 'Vel ein chat'} className="max-h-32 min-h-10 flex-1 resize-y bg-transparent px-2 py-2 text-sm text-white outline-none placeholder:text-white/30 disabled:cursor-not-allowed" />
                    <button type="submit" data-testid="send-chat-message-button" disabled={sending || !messageText.trim() || !activeChannel} title="Send melding" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#ff2e2e] text-black transition hover:bg-[#ff5555] disabled:cursor-not-allowed disabled:opacity-40"><Send size={17} /></button>
                  </div>
                  <p className="mt-1.5 px-1 text-[10px] text-white/30">Enter sender · Shift + Enter for linjeskift</p>
                </form>
              </section>
            </div>
          </>
        )}
      </main>
      <Footer />

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent data-testid="create-chat-group-dialog" className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto rounded-2xl border-white/10 bg-[#100c14] text-white [&>button]:text-white">
          <DialogTitle className="text-xl font-bold">Opprett medlemsgruppe</DialogTitle>
          <DialogDescription className="text-sm text-white/50">Du får ein gruppekode du kan dele med andre medlemmer.</DialogDescription>
          <form onSubmit={createGroup} className="space-y-4">
            <label className="block text-xs font-bold uppercase tracking-wider text-white/65">Gruppenamn<input data-testid="chat-group-name" required minLength={2} maxLength={60} value={groupName} onChange={(event) => setGroupName(event.target.value)} className={`${inputClass} mt-1.5 normal-case tracking-normal`} placeholder="Til dømes Bilprat" /></label>
            <label className="block text-xs font-bold uppercase tracking-wider text-white/65">Kort skildring<textarea maxLength={500} rows={2} value={groupDescription} onChange={(event) => setGroupDescription(event.target.value)} className={`${inputClass} mt-1.5 resize-y normal-case tracking-normal`} placeholder="Kva handlar gruppa om?" /></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-white/65">Logo<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => setGroupLogo(event.target.files?.[0] || null)} className="mt-2 block w-full text-xs text-white/55 file:mr-2 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white" /></label>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/65">Banner<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => setGroupBanner(event.target.files?.[0] || null)} className="mt-2 block w-full text-xs text-white/55 file:mr-2 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white" /></label>
            </div>
            {dialogError && <p role="alert" className="text-sm text-red-300">{dialogError}</p>}
            <button type="submit" disabled={dialogBusy || !groupName.trim()} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2e2e] px-5 py-3 font-bold text-black disabled:opacity-50"><Plus size={17} />{dialogBusy ? 'Opprettar…' : 'Opprett gruppe'}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent data-testid="join-chat-group-dialog" className="w-[calc(100vw-2rem)] max-w-md rounded-2xl border-white/10 bg-[#100c14] text-white [&>button]:text-white">
          <DialogTitle className="text-xl font-bold">Bli med i medlemsgruppe</DialogTitle>
          <DialogDescription className="text-sm text-white/50">Skriv inn gruppekoden du har fått frå eit medlem.</DialogDescription>
          <form onSubmit={joinGroup} className="space-y-4">
            <label htmlFor="chat-group-code" className="sr-only">Gruppekode</label>
            <input id="chat-group-code" data-testid="chat-group-code" required minLength={6} maxLength={16} autoCapitalize="characters" value={joinCode} onChange={(event) => setJoinCode(event.target.value.toUpperCase())} className={`${inputClass} text-center font-mono text-xl tracking-[0.18em]`} placeholder="ABCD2345" />
            {dialogError && <p role="alert" className="text-sm text-red-300">{dialogError}</p>}
            <button type="submit" disabled={dialogBusy || joinCode.trim().length < 6} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2e2e] px-5 py-3 font-bold text-black disabled:opacity-50"><Users size={17} />{dialogBusy ? 'Kontrollerer…' : 'Bli med i gruppe'}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
        <DialogContent data-testid="create-chat-channel-dialog" className="w-[calc(100vw-2rem)] max-w-md rounded-2xl border-white/10 bg-[#100c14] text-white [&>button]:text-white">
          <DialogTitle className="text-xl font-bold">Opprett chat</DialogTitle>
          <DialogDescription className="text-sm text-white/50">Lag ein ny tekstchat i {activeGroup?.name || 'gruppa'}.</DialogDescription>
          <form onSubmit={createChannel} className="space-y-4">
            <label htmlFor="chat-channel-name" className="block text-xs font-bold uppercase tracking-wider text-white/65">Namn på chatten<input id="chat-channel-name" data-testid="chat-channel-name" required minLength={2} maxLength={50} value={channelName} onChange={(event) => setChannelName(event.target.value)} className={`${inputClass} mt-1.5 normal-case tracking-normal`} placeholder="Til dømes planlegging" /></label>
            {dialogError && <p role="alert" className="text-sm text-red-300">{dialogError}</p>}
            <button type="submit" disabled={dialogBusy || !channelName.trim()} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2e2e] px-5 py-3 font-bold text-black disabled:opacity-50"><Plus size={17} />{dialogBusy ? 'Opprettar…' : 'Opprett chat'}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteRequestDialogOpen} onOpenChange={setDeleteRequestDialogOpen}>
        <DialogContent data-testid="request-group-deletion-dialog" className="w-[calc(100vw-2rem)] max-w-md rounded-2xl border-white/10 bg-[#100c14] text-white [&>button]:text-white">
          <DialogTitle className="text-xl font-bold">Be om å slette gruppa</DialogTitle>
          <DialogDescription className="text-sm text-white/50">Administratorane får ein e-post og må godkjenne slettinga av {activeGroup?.name || 'gruppa'}.</DialogDescription>
          <form onSubmit={requestGroupDeletion} className="space-y-4">
            <label htmlFor="group-deletion-reason" className="block text-xs font-bold uppercase tracking-wider text-white/65">Grunngjeving <span className="font-normal normal-case text-white/35">(valfri)</span><textarea id="group-deletion-reason" maxLength={1000} rows={3} value={deleteRequestReason} onChange={(event) => setDeleteRequestReason(event.target.value)} className={`${inputClass} mt-1.5 resize-y normal-case tracking-normal`} placeholder="Kvifor ønskjer du å slette gruppa?" /></label>
            {dialogError && <p role="alert" className="text-sm text-red-300">{dialogError}</p>}
            <button type="submit" disabled={dialogBusy} className="flex w-full items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-3 font-bold text-white disabled:opacity-50"><Trash2 size={16} />{dialogBusy ? 'Sender…' : 'Send sletteførespurnad'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberCenter;